import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  deleteExpenses,
  EXPENSES_SEARCH_INITIAL_VALUE,
  getAllExpenses,
  getExpensesFields,
  IExpenses,
  IExpensesSearch,
} from "./ExpensesViewController";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  deleteIcon,
  editIcon,
  eyeIcon,
  filterIcon,
  galleryImageIcon,
} from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICImage,
  ICModal,
  ICTable,
} from "../../core-component/ui";
import {
  getLocalDate,
  prepareMessageFromParams,
} from "../../utils/AppFunctions";
import {
  ORDER_BY,
  PAYMENT_METHOD_STATUS_OPTION,
} from "../../utils/AppConstants";
import ExpensesForm from "./ExpensesForm";
import {
  ICDatePicker,
  ICDropDown,
  ICTextInput,
} from "../../core-component/form";
import { getExpenseHeadDdl } from "./ExpensesFormController";

let timer: ReturnType<typeof setTimeout>;

const ExpensesView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [readOnly, setReadOnly] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [expensesList, setExpensesList] = useState<IExpenses[]>([]);
  const { pagination, setChangePagination } = UsePaginationHook();
  const [expensesToView, setExpensesToView] = useState<IExpenses>();
  const [advanceSearch, setAdvanceSearch] = useState<IExpensesSearch>(
    EXPENSES_SEARCH_INITIAL_VALUE,
  );
  const [isShowProofModal, setIsShowProofModal] = useState(false);
  const [expenses, setExpenses] = useState<IOption<string>[]>([]);

  useEffect(() => {
    fetchExpenses({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);
  useEffect(() => {
    fetchExpenseHead();
  }, []);
  const fetchExpenses = (paginationPlayLoad: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await getAllExpenses(
        setExpensesList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch,
      );
      setIsLoading(false);
    }, 500);
  };
  const customActionView = (item: IExpenses) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className=" size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setExpensesToView(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>

        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("edit_tooltip_icon")}
          onClick={() => {
            setShowForm(true);
            setExpensesToView(item);
          }}
        >
          {editIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("delete_tooltip_icon")}
          onClick={() => {
            setShowDeleteConfirmation(true);
            setExpensesToView(item);
          }}
        >
          {deleteIcon}
        </div>
        {item.receipt_media_url && (
          <div
            className="ml-2 size-5 cursor-pointer"
            title={t("view_receipt_tooltip")}
            onClick={() => {
              setIsShowProofModal(true);
              setExpensesToView(item);
            }}
          >
            {galleryImageIcon}
          </div>
        )}
      </div>
    );
  };
  const handleDelete = async (value: boolean) => {
    if (value && expensesToView) {
      const data = await deleteExpenses(expensesToView.id);
      if (data) {
        fetchExpenses(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setExpensesToView(undefined);
  };

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnly(false);
    if (expensesToView) {
      setExpensesToView(undefined);
    }
    if (status) {
      fetchExpenses(pagination);
    }
  };
  const fetchExpenseHead = async () => {
    await getExpenseHeadDdl(setExpenses);
  };
  const filterView = (
    <div className="w-full grid lg:grid-cols-3 sm:grid-cols-1 gap-4 mt-3">
      <ICDropDown
        placeholder={t("expense_head")}
        selected={advanceSearch.expenseHead}
        options={expenses}
        onSelect={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            expenseHead: option.data_value,
          }))
        }
      />
      <ICDatePicker
        useValue
        label={t("expense_from_date")}
        placeholder={t("expense_from_date")}
        value={
          advanceSearch.expenseFromDate !== ""
            ? getLocalDate(advanceSearch.expenseFromDate)
            : undefined
        }
        onChangeDate={(event) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            expenseFromDate: event.target.value,
          }))
        }
      />
      <ICDatePicker
        useValue
        label={t("expense_to_date")}
        min={getLocalDate(advanceSearch.expenseToDate)}
        placeholder={t("expense_to_date")}
        value={
          advanceSearch.expenseToDate !== ""
            ? getLocalDate(advanceSearch.expenseToDate)
            : undefined
        }
        onChangeDate={(event) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            expenseToDate: event.target.value,
          }))
        }
      />
      <ICTextInput
        type="search"
        placeholder={t("transaction_reference_no")}
        onChange={(event) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            transactionReference: event.target.value,
          }))
        }
        value={advanceSearch.transactionReference}
      />
      <ICDropDown
        placeholder={t("payment_method")}
        selected={advanceSearch.paymentMethod}
        options={PAYMENT_METHOD_STATUS_OPTION}
        onSelect={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            paymentMethod: option.data_value,
          }))
        }
      />

      <ICButton
        onClick={() => setAdvanceSearch(EXPENSES_SEARCH_INITIAL_VALUE)}
        className="flex !w-fit"
      >
        <div className="size-5 grid place-self-center">{filterIcon}</div>
        &nbsp;{t("clear")}
      </ICButton>
    </div>
  );
  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        filterNode={filterView}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchExpenses(pagination);
        }}
        title={t("expenses")}
      />

      <ICTable<IExpenses>
        isLoading={isLoading}
        totalFlex="grid grid-cols-13"
        fields={getExpensesFields(customActionView, t)}
        data={expensesList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchExpenses({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchExpenses({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchExpenses({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showForm && (
        <ExpensesForm
          handleClose={handleCloseForm}
          expensesToEdit={expensesToView}
          readOnly={readOnly}
        />
      )}
      {isShowProofModal && expensesToView && (
        <ICModal
          title={t("view_receipt_tooltip")}
          onClose={() => setIsShowProofModal(false)}
          children={
            <ICImage
              containerClass="bg-subtle flex-1 max-h-[calc(80vh-32px)] aspect-square"
              imageUrl={expensesToView.receipt_media_url}
              className="w-full h-full aspect-square object-contain"
            />
          }
        />
      )}
      {showDeleteConfirmation && expensesToView && (
        <ICConfirmationModal
          title={t("delete_expenses")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", expensesToView.title],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default ExpensesView;
